import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { RootState } from "../../../store";
import { fetchReferralProductDetail } from "../../../Redux/RefProduct/slice";
import { recordActivity } from "../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../Redux/ActivityApi/types";
import { useCart } from "../../../context/CartContext";
import { useWishlist } from "../../../context/WishlistContext";
import { useAuth } from "../../../context/AuthContext";
import { ProductType } from "../../../Redux/custmoer_cart_service/types";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import ProductBreadcrumbs from "../../globalcomponents/ProductBreadcrumbs/ProductBreadcrumbs";
import SideCart from "../../globalcomponents/SideCart/SideCart";
import MobileProductStickyActions from "../../globalcomponents/mobile/MobileProductStickyActions/MobileProductStickyActions";
import ProductImageGallery from "./ProductImageGallery/ProductImageGallery";
import ProductDetailCenter from "./ProductDetailCenter/ProductDetailCenter";
import ProductPurchaseCard from "./ProductPurchaseCard/ProductPurchaseCard";
import OtherShoppingVouchers from "./OtherShoppingVouchers/OtherShoppingVouchers";
import {
  mapReferralToProductDetail,
  RefProductDetailData,
} from "./RefProductDetailTypes";
import "./Refproductdetails.css";

const Refproductdetails = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart, setCartOpen, cartCount } = useCart();
  const { toggleWishlist } = useWishlist();

  const { currentProduct, loading, error } = useAppSelector(
    (state: RootState) => state.refProduct
  );

  // Fetch product data on id change
  useEffect(() => {
    if (id) {
      dispatch(fetchReferralProductDetail(id));
    }
  }, [dispatch, id]);

  // Record view activity after 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (currentProduct && id) {
      timer = setTimeout(() => {
        dispatch(
          recordActivity({
            activities: {
              eventType: ActivityType.VIEW_PRODUCT,
              productId: id,
            },
          })
        );
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [currentProduct, id, dispatch]);

  // Map API response to clean detail data structure
  const product: RefProductDetailData | null = useMemo(() => {
    if (!currentProduct || !currentProduct.product) return null;
    return mapReferralToProductDetail(currentProduct);
  }, [currentProduct]);

  // Cart action handler with referral voucher flags
  const handleAddToCart = async (quantity = 1) => {
    if (!isAuthenticated) {
      navigate("/SignIn");
      return;
    }
    if (!product) return;

    try {
      await addToCart({
        productId: String(product.productId || product.id),
        productName: product.title,
        productImage: product.image,
        productType: ProductType.MLM_VOUCHER,
        variantId: product.ssin || product.productId || product.id,
        variantName: product.title,
        quantity,
        unitPrice: product.price,
        price: product.price,
        mrp: product.mrp,
        sellerId: product.sellerId || product.brand,
        sellerName: product.sellerName !== "-" ? product.sellerName : undefined,
        isMLMProduct: true,
        metadata: {
          mrp: product.mrp,
          image: product.image,
          description: product.description,
        },
      });
      setCartOpen(true);
    } catch (err: any) {
      toast.error(err?.message || "Failed to add product to cart");
    }
  };

  // Buy Now handler
  const handleBuyNow = async (quantity = 1) => {
    if (!isAuthenticated) {
      navigate("/SignIn");
      return;
    }
    if (!product) return;

    try {
      await addToCart({
        productId: String(product.productId || product.id),
        productName: product.title,
        productImage: product.image,
        productType: ProductType.MLM_VOUCHER,
        variantId: product.ssin || product.productId || product.id,
        variantName: product.title,
        quantity,
        unitPrice: product.price,
        price: product.price,
        mrp: product.mrp,
        sellerId: product.sellerId || product.brand,
        sellerName: product.sellerName !== "-" ? product.sellerName : undefined,
        isMLMProduct: true,
        metadata: {
          mrp: product.mrp,
          image: product.image,
          description: product.description,
        },
      });
      navigate("/Cart");
    } catch (err: any) {
      toast.error(err?.message || "Failed to proceed to checkout");
    }
  };

  // Wishlist toggle handler
  const handleWishlist = () => {
    if (!product) return;
    toggleWishlist(String(product.id), {
      ssin: product.ssin || undefined,
      productVariantId: product.ssin || undefined,
    });
  };

  // Loading skeleton state
  if (loading) {
    return (
      <>
        <div className="product-detail-desktop-navbar">
          <HomeNavbar variant="home" />
        </div>
        <MobileDetailNavbar cartCount={cartCount} />

        <div className="product-detail-placeholder">
          <div className="product-detail-skeleton-img" />
          <div className="product-detail-skeleton-content">
            <div
              className="product-detail-skeleton-line"
              style={{ width: "85%", height: 32 }}
            />
            <div
              className="product-detail-skeleton-line"
              style={{ width: "50%", height: 22 }}
            />
            <div
              className="product-detail-skeleton-line"
              style={{ width: "35%", height: 38, marginTop: 18 }}
            />
            <div
              className="product-detail-skeleton-line"
              style={{ width: "100%", height: 180, marginTop: 24 }}
            />
          </div>
          <div
            className="product-detail-skeleton-line"
            style={{ width: "100%", height: 420 }}
          />
        </div>
      </>
    );
  }

  // Not found or error state
  if (!product || error) {
    return (
      <>
        <div className="product-detail-desktop-navbar">
          <HomeNavbar variant="home" />
        </div>
        <MobileDetailNavbar cartCount={cartCount} />

        <div className="product-detail-not-found">
          <h2>Product Not Found</h2>
          <p>
            {error ||
              "The requested referral product details could not be retrieved."}
          </p>
          <button type="button" onClick={() => navigate("/HomePage")}>
            <ArrowLeft size={16} />
            Back to Home
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Desktop Header */}
      <div className="product-detail-desktop-navbar">
        <HomeNavbar variant="home" />
      </div>

      {/* Mobile Header */}
      <MobileDetailNavbar cartCount={cartCount} />

      {/* Breadcrumb Navigation */}
      <ProductBreadcrumbs items={product.breadcrumb} />

      {/* Main 3-Column Product Detail Layout */}
      <main className="product-detail-page">
        <div className="product-detail-container">
          {/* Left Column: Image Gallery */}
          <div className="product-detail-left">
            <ProductImageGallery
              title={product.title}
              image={product.image}
              images={product.images}
              description={product.description}
            />
          </div>

          {/* Center Column: Title, Voucher Info, Pricing, Offers & Details */}
          <div className="product-detail-center">
            <ProductDetailCenter product={product} />

            {/* Mobile View Purchase Card placed directly under center content */}
            <div className="product-detail-mobile-purchase">
              <ProductPurchaseCard
                productId={product.id}
                productTitle={product.title}
                productImage={product.image}
                productDescription={product.description}
                price={product.price}
                mrp={product.mrp}
                ssin={product.ssin}
                sellerName={product.sellerName}
                sellerSlug={product.sellerSlug}
                sellerId={product.sellerId}
                stock={product.stockStatus !== "out_of_stock"}
                packaging={product.packaging}
                payment={product.payment}
                giftOptions={product.giftOptions}
                category={product.category}
                returnPolicy={product.returnPolicy}
                warranty={product.warranty}
                voucherAmount={product.voucherAmount}
                voucherValidityDays={product.voucherValidityDays}
                autoRepurchaseAmount={product.autoRepurchaseAmount}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
                onWishlist={handleWishlist}
              />
            </div>
          </div>

          {/* Right Column: Amazon-style Buy Box Card */}
          <aside className="product-detail-right">
            <ProductPurchaseCard
              productId={product.id}
              productTitle={product.title}
              productImage={product.image}
              productDescription={product.description}
              price={product.price}
              mrp={product.mrp}
              ssin={product.ssin}
              sellerName={product.sellerName}
              sellerSlug={product.sellerSlug}
              sellerId={product.sellerId}
              stock={product.stockStatus !== "out_of_stock"}
              packaging={product.packaging}
              payment={product.payment}
              giftOptions={product.giftOptions}
              category={product.category}
              returnPolicy={product.returnPolicy}
              warranty={product.warranty}
              voucherAmount={product.voucherAmount}
              voucherValidityDays={product.voucherValidityDays}
              autoRepurchaseAmount={product.autoRepurchaseAmount}
              onAddToCart={handleAddToCart}
              onBuyNow={handleBuyNow}
              onWishlist={handleWishlist}
            />
          </aside>
        </div>

        {/* Other Shopping Vouchers Section */}
        {product.otherShoppingVouchers &&
          product.otherShoppingVouchers.length > 0 && (
            <OtherShoppingVouchers vouchers={product.otherShoppingVouchers} />
          )}
      </main>

      {/* Sticky Mobile Add to Cart / Buy Now Bar */}
      <MobileProductStickyActions
        onAddToCart={() => handleAddToCart(1)}
        onBuyNow={() => handleBuyNow(1)}
      />

      {/* Global Slide-in Cart Drawer */}
      <SideCart />
    </>
  );
};

export default Refproductdetails;
